import React, { useMemo } from 'react';

import delv from 'dlv';
import { defineMessages, useIntl } from 'react-intl';
import { Link } from 'react-router-dom';
import styled from 'styled-components';

import { ILoyaltyCustomizableContent } from '@rbi-ctg/menu';
import CardDeactivationModal from 'components/card-deactivation-modal';
import Picture from 'components/picture';
import BlockRenderer from 'components/sanity-block-renderer';
import TextButton from 'components/text-button';
import { useLoyaltyActiveCard } from 'hooks/loyalty';
import { LOYALTY_ERROR_PLACEHOLDER } from 'hooks/loyalty/constants';
import useDialogModal from 'hooks/use-dialog-modal';
import useEffectOnUpdates from 'hooks/use-effect-on-updates';
import useErrorModal from 'hooks/use-error-modal';
import useMediaQuery from 'hooks/use-media-query';
import useRoutes from 'hooks/use-routes';
import { useFlag } from 'state/launchdarkly';
import { LaunchDarklyFlag } from 'utils/launchdarkly';

import Registration from './registration';

const Container = styled.div`
  max-width: ${Styles.layout.sectionMaxWidth};
  margin: 0 auto;
  padding: 1.2rem;
  line-height: 1.4em;

  ${Styles.desktop`
    flex-direction: row;
  `};
`;

const PointDetails = styled.div`
  font-size: 1.5rem;
  border: 1px solid ${Styles.color.grey.seven};
  border-radius: 6px;
  padding: 0.8rem 1.6rem;
  margin: -30px auto 0;
  z-index: ${Styles.zIndex.below};
  background-color: ${Styles.color.white};
`;

const PointTotal = styled.span`
  font-weight: ${Styles.fontWeight.black};
  color: ${Styles.color.primary};
`;

const SubSection = styled.section`
  margin: 2rem 0;
  padding: 0 1rem;
  border-top: 1px solid ${Styles.color.grey.four};
`;

const SubSectionTitle = styled.div`
  margin-top: 2rem;
  margin-bottom: 1.2rem;
  font-size: 1.2rem;
  font-weight: ${Styles.fontWeight.normal};
  text-align: center;
`;

const SubSectionContent = styled.div<{ centered?: boolean }>`
  font-size: 1rem;
  margin: 1rem;
  color: ${Styles.color.grey.two};
  text-align: ${({ centered }) => (centered ? 'center' : 'left')};
`;

const TimsRewardsTitle = styled(SubSectionTitle)`
  height: 100px;
  margin-top: 0.2rem;
  margin-bottom: 2.2rem;
`;

const TimsRewardsIcon = styled(Picture)`
  margin-right: 40px;
  flex: 0 0 40px;
`;

const TimsRewardsPoint = styled.div`
  color: ${Styles.color.black};
  flex: 1 1 240px;
`;

const TimsRewardsItem = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 1.2rem;
`;

const UserAccountNumberText = styled(SubSectionContent)`
  text-align: center;
`;

const StyledLink = styled(Link)`
  color: ${Styles.color.grey.two};
`;

const CardNumber = styled.p`
  font-weight: ${Styles.fontWeight.heavy};
`;

export function generateCardIdLabel(cardId: string) {
  return cardId.length !== 12
    ? cardId
    : `${cardId.slice(0, 4)}-${cardId.slice(4, 8)}-${cardId.slice(8, 12)}`;
}
const HeroImageWrapper = styled.div`
  padding-bottom: 20px;
  background-color: ${Styles.color.primary};
  height: calc(min(400px, 80vw));

  ${Styles.desktop`
    img {
      object-fit: contain;
    }
  `}
`;

export type IDetailsProps = {
  loyaltyContent: ILoyaltyCustomizableContent;
};

const Details = ({ loyaltyContent }: IDetailsProps) => {
  const { formatMessage } = useIntl();
  const { error: activeCardError, activeCard, loyaltyDetails } = useLoyaltyActiveCard();
  const enableVoucher = useFlag(LaunchDarklyFlag.ENABLE_TH_VOUCHER_CODES);
  const [ErrorDialog, openErrorDialog] = useErrorModal({
    modalAppearanceEventMessage: 'Error: Failed to get user loyalty'
  });
  const [CardDeactivationDialog, showCardDeactivationModal] = useDialogModal({
    Component: CardDeactivationModal
  });
  const { getLocalizedRouteForPath } = useRoutes();

  const { cardId = '' } = activeCard || {};
  const userAccountNumber = useMemo(() => generateCardIdLabel(cardId), [cardId]);

  useEffectOnUpdates(() => {
    openErrorDialog({
      message: formatMessage(messages.loadLoyaltyError),
      error: activeCardError
    });
  }, [activeCardError]);

  const isMobile = useMediaQuery('mobile');
  const detailsPageHeroImage = isMobile
    ? delv(loyaltyContent, 'detailsPageHeroImageMobile.locale')
    : delv(loyaltyContent, 'detailsPageHeroImageWeb.locale');
  const timsRewardsImage = delv(loyaltyContent, 'timsRewardsImage.locale');
  const detailsInstructionsOne = delv(loyaltyContent, 'detailsInstructionsOne.locale');
  const detailsInstructionsTwo = delv(loyaltyContent, 'detailsInstructionsTwo.locale');
  const detailsInstructionsThree = delv(loyaltyContent, 'detailsInstructionsThree.locale');
  const detailsInstructionsFour = delv(loyaltyContent, 'detailsInstructionsFour.locale');

  return (
    <>
      <HeroImageWrapper>
        {detailsPageHeroImage && (
          <Picture
            image={detailsPageHeroImage}
            alt={formatMessage(messages.loyaltyDetailsHeroImage)}
          />
        )}
      </HeroImageWrapper>
      <PointDetails data-testid="rewards-points-detail-message">
        {formatMessage(messages.rewardsPointsDetailsMessage, {
          points: (
            <PointTotal>
              {loyaltyDetails ? loyaltyDetails.points : LOYALTY_ERROR_PLACEHOLDER}
            </PointTotal>
          )
        })}
      </PointDetails>
      <Container>
        <TimsRewardsTitle>
          {timsRewardsImage && (
            <Picture
              image={timsRewardsImage}
              alt={formatMessage(messages.timsRewards)}
              objectFitContain
            />
          )}
        </TimsRewardsTitle>
        <SubSectionContent>
          <TimsRewardsItem>
            <TimsRewardsIcon
              image={delv(loyaltyContent, 'detailsIconOne.locale')}
              alt={detailsInstructionsOne}
            />
            <TimsRewardsPoint>{detailsInstructionsOne}</TimsRewardsPoint>
          </TimsRewardsItem>
          <TimsRewardsItem>
            <TimsRewardsIcon
              image={delv(loyaltyContent, 'detailsIconTwo.locale')}
              alt={detailsInstructionsTwo}
            />
            <TimsRewardsPoint>{detailsInstructionsTwo}</TimsRewardsPoint>
          </TimsRewardsItem>

          <TimsRewardsItem>
            <TimsRewardsIcon
              image={delv(loyaltyContent, 'detailsIconThree.locale')}
              alt={detailsInstructionsThree}
            />
            <TimsRewardsPoint>{detailsInstructionsThree}</TimsRewardsPoint>
          </TimsRewardsItem>

          <TimsRewardsItem>
            <TimsRewardsIcon
              image={delv(loyaltyContent, 'detailsIconFour.locale')}
              alt={detailsInstructionsFour}
            />
            <TimsRewardsPoint>{detailsInstructionsFour}</TimsRewardsPoint>
          </TimsRewardsItem>
          <StyledLink
            data-testid="terms-conditions-rewards-link"
            to={getLocalizedRouteForPath('terms-conditions-rewards')}
          >
            {formatMessage(messages.termsConditionsRewards)}
          </StyledLink>
        </SubSectionContent>
        <SubSection>
          <SubSectionTitle>
            {delv(loyaltyContent, 'registerCardSectionTitle.locale')}
          </SubSectionTitle>
          <SubSectionContent>
            <BlockRenderer
              content={delv(loyaltyContent, 'registerCardSectionBlockText.localeRaw')}
            />
            {activeCard && (
              <CardNumber>
                {formatMessage(messages.yourCurrentTimsRewardCardNumberIs, {
                  userAccountNumber: generateCardIdLabel(activeCard.cardId)
                })}
              </CardNumber>
            )}
            <Registration activeCard={activeCard} enableVoucher={enableVoucher} />
          </SubSectionContent>
        </SubSection>
        {enableVoucher && (
          <SubSection>
            <SubSectionTitle>{formatMessage(messages.enterAPromoCode)}</SubSectionTitle>
            <SubSectionContent>
              <p>{formatMessage(messages.promoCodeBodyDetails)}</p>
              <Registration
                activeCard={activeCard}
                enableVoucher={enableVoucher}
                buttonText={formatMessage(messages.promoCodeSubmitBtnText)}
                isVoucher
              />
            </SubSectionContent>
          </SubSection>
        )}
        <SubSection>
          <SubSectionTitle>
            {delv(loyaltyContent, 'loyaltyCardSectionTitle.locale')}
          </SubSectionTitle>
          <UserAccountNumberText>{userAccountNumber}</UserAccountNumberText>
        </SubSection>
        <SubSection>
          <SubSectionTitle>{delv(loyaltyContent, 'lostCardSectionTitle.locale')}</SubSectionTitle>
          <SubSectionContent>
            <BlockRenderer content={delv(loyaltyContent, 'lostCardSectionBlockText.localeRaw')} />
          </SubSectionContent>
          <SubSectionContent centered>
            <TextButton data-testid="deactivate-card-link" onClick={showCardDeactivationModal}>
              {formatMessage(messages.deactivateCard)}
            </TextButton>
          </SubSectionContent>
        </SubSection>
      </Container>
      <CardDeactivationDialog />
      <ErrorDialog />
    </>
  );
};

export default Details;
const messages = defineMessages({
  deactivateCard: {
    id: 'deactivateCard',
    defaultMessage: 'Deactivate Card',
    description: 'Button to deactivate their card'
  },

  yourCurrentTimsRewardCardNumberIs: {
    id: 'yourCurrentTimsRewardCardNumberIs',
    description: 'Text that labels card number'
  },

  termsConditionsRewards: {
    id: 'termsConditionsRewards',
    defaultMessage: 'Terms and Conditions',
    description: 'Link to tak user to their terms and conditions'
  },

  timsRewards: {
    id: 'timsRewards',
    defaultMessage: 'Tims Rewards',
    description: 'Tims Rewards Image Alt Text'
  },

  rewardsPointsDetailsMessage: {
    id: 'rewardsPointsDetailsMessage',
    defaultMessage: 'You have {points} points',
    description: 'Shows the user how many rewards points they have'
  },

  loyaltyDetailsHeroImage: {
    id: 'loyaltyDetailsHeroImage',
    defaultMessage: 'Get More Of Your Favourite Things For Free',
    description: 'Loyalty Details Hero Image Alt Text'
  },

  loadLoyaltyError: {
    id: 'loadLoyaltyError',
    defaultMessage: 'There was an error loading your loyalty card.',
    description: 'Error message when an error occurs while loading loyalty'
  },

  enterAPromoCode: {
    id: 'enterAPromoCode',
    defaultMessage: 'Enter a Promo Code',
    description: 'Header asking guest to enter a promo code'
  },

  promoCodeBodyDetails: {
    id: 'promoCodeBodyDetails',
    defaultMessage: 'Please enter the 12 digit promo code below',
    description: 'Body text asking guest to enter the 12 digit promo code'
  },

  promoCodeSubmitBtnText: {
    id: 'promoCodeSubmitBtnText',
    defaultMessage: 'Submit Code',
    description: 'Promotion Code Button Text'
  }
});
